import { AppLayout } from '@/components/layout/app-layout'
import LoginPage from '@/pages/auth/login-page'
import ActivityPage from '@/pages/customer/activity-page'
import CallsPage from '@/pages/customer/calls-page'
import ForwardPage from '@/pages/customer/forward-page'
import KnowledgePage from '@/pages/customer/knowledge-page'
import MdsConfigurationPage from '@/pages/customer/mds-configuration-page'
import NotFoundPage from '@/pages/not-found-page'
import { useAuth } from '@/providers/auth-provider'
import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

// Lazy load MDS pages
const MdsActivityPage = lazy(() => import('@/pages/customer/mds-activity-page'))
const MdsCallsPage = lazy(() => import('@/pages/customer/mds-calls-page'))

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: string[]
}

function ProtectedRoute({ children, requiredRoles = [] }: ProtectedRouteProps) {
  const { user, isAuthenticated } = useAuth()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (requiredRoles.length > 0 && user) {
    const hasRequiredRole = requiredRoles.some(role =>
      user.roles?.includes(role)
    )
    if (!hasRequiredRole) {
      return <Navigate to="/" replace />
    }
  }

  return <>{children}</>
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/customer/activity" replace />} />

        {/* Customer Routes */}
        <Route
          path="customer/activity"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <ActivityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/calls"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <CallsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/knowledge"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <KnowledgePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/knowledge/:categoryId"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <KnowledgePage />
            </ProtectedRoute>
          }
        />
        {/* Legacy route support */}
        <Route
          path="customer/answers"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <KnowledgePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/answers/:categoryId"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <KnowledgePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/forward"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <ForwardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/forward/profile/:profileId"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <ForwardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/forward/recipient/:recipientId"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <ForwardPage />
            </ProtectedRoute>
          }
        />

        {/* MDS Routes */}
        <Route
          path="customer/mds-activity"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div></div>}>
                <MdsActivityPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/mds-calls"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div></div>}>
                <MdsCallsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="customer/mds-configuration"
          element={
            <ProtectedRoute requiredRoles={["ROLE_ACCESS_CUSTOMERFRONTEND", "ROLE_ACCESS_ADMINFRONTEND"]}>
              <MdsConfigurationPage />
            </ProtectedRoute>
          }
        />

        {/* Legacy MDS route - redirect to new route */}
        <Route
          path="customer/mdsConfiguration"
          element={<Navigate to="/customer/mds-configuration" replace />}
        />
      </Route>

      {/* 404 Route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
